import {
  CustomButton,
  CustomTable,
  SkeletonLoader,
  SvgEmptyTable,
  TableColumnType,
} from '@ui-kit'
import { useEffect, useState } from 'react'
import {
  addToaster,
  hideLoader,
  showLoader,
  addEPRPatient,
  getGenericEntityListingAPI,
} from '@app-store'
import { NAMESPACES } from '../../utils/constants'
import { FieldValues } from 'react-hook-form'
import { editEntityDataEmbeddedAPI } from '../../apis/entityData.api'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import { IEPRData } from '../../types/workflow.types'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { uniqueId } from 'lodash'

const EPRQueryModal: React.FC<{
  patientData: FieldValues
  metaData?: { layout_id: string; entity_id: string; entity_name: string }
  closeModal: () => void
  pharmacyEntityName?: string
}> = ({ patientData, closeModal, pharmacyEntityName }) => {
  const dispatch = useAppDispatch()
  const params = useParams()
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const columns: TableColumnType<IEPRData>[] = [
    {
      header: '',
      accessor: 'select',
      type: 'radio',
    },
    {
      header: NAMESPACES.PATIENT_FIRST_NAME,
      accessor: 'patient_first_name',
    },
    {
      header: NAMESPACES.PATIENT_LAST_NAME,
      accessor: 'patient_last_name',
    },
    {
      header: NAMESPACES.PATIENT_DOB,
      accessor: 'patient_dob',
    },
    {
      header: NAMESPACES.PHARMACY_ID,
      accessor: 'pharmacy_id',
    },
    {
      header: NAMESPACES.ZIPCODE,
      accessor: 'zipcode',
    },
    {
      header: NAMESPACES.ADDRESS_LINE_1,
      accessor: 'address_line_1',
    },
    {
      header: NAMESPACES.ADDRESS_LINE_2,
      accessor: 'address_line_2',
    },
  ]

  const [data, setData] = useState<IEPRData[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true)

  const onColumnClick = (
    _isHeader: boolean,
    KeyName: keyof IEPRData,
    _row?: IEPRData | null,
    rowIndex: number | null = null
  ) => {
    switch (KeyName) {
      case 'select':
        if (rowIndex !== null) {
          selectRow(rowIndex)
          setSaveDisabled(false)
        }
    }
  }

  const selectRow = (index: number) => {
    setData((prevData) =>
      prevData.map((item, i) => ({
        ...item,
        select: i === index,
      }))
    )
    setSaveDisabled(false)
  }

  const fetchEPRPatients = async () => {
    try {
      setLoading(true)
      const payload = {
        select: ['id', 'patient_pharmacy'],
        related_entities: {
          patient_pharmacy: {
            select: ['id', 'pharmacy_id'],
            criteria: [],
          },
        },
        criteria: [],
        filters: [
          {
            criteria: [
              {
                field_name: 'record_status',
                values: ['ACTIVE'],
                operation: 'EQUALS',
              },
            ],
          },
        ],
        entity_name:
          workflowDefinition?.global_variables?.entities?.patients
            ?.entity_name ?? '',
        entity_id:
          workflowDefinition?.global_variables?.entities?.patients?.entity_id ??
          '',
      }
      const caseIdObject2 = await getGenericEntityListingAPI(
        payload,
        patientData?.patient?.id ?? ''
      )
      const listData =
        caseIdObject2?.patient_pharmacy?.map((item: FieldValues) => ({
          id: item?.id,
          pharmacy_id: item?.pharmacy_id,
          patient_first_name: patientData?.patient?.first_name ?? '',
          patient_last_name: patientData?.patient?.last_name ?? '',
          patient_dob: patientData?.patient?.date_of_birth ?? '',
        })) ?? []
      if (listData?.length) setData(listData)
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddPatient = async () => {
    try {
      dispatch(showLoader())
      const response = await addEPRPatient(
        parseInt(params?.caseId ?? '0'),
        patientData?.patient?.id
      )
      if (response) {
        fetchEPRPatients()
      }
    } catch (err) {
      const status = (err as AxiosError).response?.status
      const data = (err as AxiosError)?.response?.data
      const message =
        (data as unknown as { message: string })?.message ??
        (data as unknown as { body: Record<string, FieldValues> })?.body
          ?.RxcomFailureInfo?.RxcomFailureDescription?._text ??
        ''
      if (status === 400) {
        dispatch(
          addToaster({
            id: uniqueId(),
            message,
            type: 'error',
          })
        )
      }
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  const handlePatientSave = async (selectedPatient?: IEPRData) => {
    try {
      dispatch(showLoader())
      const data = {
        status: 'ACTIVE',
        _state: 'CHANGED',
      }
      const response = await editEntityDataEmbeddedAPI(
        pharmacyEntityName ?? '',
        selectedPatient?.id ?? '',
        data,
        ['status']
      )
      if (response?.data) {
        dispatch(
          addToaster({
            id: new Date().getTime()?.toString(),
            message: NAMESPACES?.DETAILS_UPDATED_SUCCESSFULLY,
            type: 'success',
          })
        )
      }
      const dispatchevent = new CustomEvent('EPR_PATIENT_UPDATED', {
        detail: { patientId: patientData?.patient?.id },
      })
      window?.dispatchEvent(dispatchevent)
      closeModal()
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoader())
    }
  }

  useEffect(() => {
    if (workflowDefinition) fetchEPRPatients()
  }, [workflowDefinition])

  if (loading) {
    return (
      <div>
        <div className="flex justify-between bg-accent-25 items-center p-2">
          <div className="flex flex-col gap-2">
            <div className="h-10 animate-pulse bg-accent-100 w-96"></div>
            <div className="h-6 animate-pulse bg-accent-100 w-48"></div>
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-24 bg-accent-100"></div>
            <div className="h-10 w-24 bg-accent-100"></div>
          </div>
        </div>
        <div className="h-[15rem] w-[60rem]">
          <SkeletonLoader rows={3} />
        </div>
      </div>
    )
  }
  return (
    <div className="max-h-[75vh]">
      <div className="flex flex-col">
        <div className="flex items-center justify-between pr-6 pl-3 py-3">
          <div className="flex flex-col">
            <span className="text-lg font-medium">
              {NAMESPACES.PHARMACY_DETAILS_FOUND}
            </span>
            <span className="font-light text-sm">
              {data?.length > 0
                ? `${data.length} ${NAMESPACES?.RESULTS_FOUND}`
                : `${NAMESPACES?.NO_RESULTS_FOUND}`}
            </span>
          </div>
          <div className="flex gap-2">
            <CustomButton
              isFilled
              variant="sm"
              disabled={!saveDisabled}
              onClick={() => {
                handleAddPatient()
              }}
            >
              {NAMESPACES?.ADD_PATIENT}
            </CustomButton>
            <CustomButton
              isFilled
              variant="sm"
              disabled={saveDisabled || data?.length === 0}
              onClick={() =>
                handlePatientSave(data.find((item) => item.select))
              }
            >
              {NAMESPACES?.SAVE}
            </CustomButton>
          </div>
        </div>
      </div>
      {data?.length === 0 ? (
        <div className="h-[15rem] bg-secondary-25 w-[50rem] flex items-center justify-center flex-col">
          <SvgEmptyTable />
          <span className="text-sm font-light leading-lg">
            {NAMESPACES?.NO_PHARMACY_DETAILS_FOUND}
          </span>
        </div>
      ) : (
        <div className="p-1 border border-accent-200 rounded">
          <CustomTable
            columns={columns}
            data={data}
            showTableColumnOnEmpty
            onRowClick={(_row, rowIndex) => {
              selectRow(rowIndex)
            }}
            onColumnClick={(isHeader, KeyName, row, rowIndex) => {
              onColumnClick(isHeader, KeyName, row, rowIndex)
            }}
            maxHeight="60vh"
          />
        </div>
      )}
    </div>
  )
}

export default EPRQueryModal
